import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiCheck, FiAlertTriangle, FiAlertCircle, FiInfo, FiX 
} from 'react-icons/fi';

import { InfoCardProps } from '../../types/components';

/**
 * InfoCard component for displaying important information in a card format
 * 
 * This component provides a styled card for displaying informational messages,
 * warnings, success messages, or other important content. It can optionally
 * be dismissible.
 */
const InfoCard: React.FC<InfoCardProps> = ({
  title,
  content,
  icon,
  color = 'blue',
  dismissible = false
}) => {
  // State to track visibility if dismissible
  const [isVisible, setIsVisible] = useState(true);
  
  // Return null if card has been dismissed
  if (!isVisible) return null;
  
  /**
   * Get CSS class for background color based on color prop
   */
  const getBackgroundClass = (color: string): string => {
    switch (color) {
      case 'green': return 'bg-green-50 border-green-200';
      case 'red': return 'bg-red-50 border-red-200';
      case 'yellow': return 'bg-yellow-50 border-yellow-200';
      case 'blue':
      default: return 'bg-blue-50 border-blue-200';
    }
  };
  
  /**
   * Get CSS class for icon color based on color prop
   */
  const getIconColorClass = (color: string): string => {
    switch (color) {
      case 'green': return 'text-green-500';
      case 'red': return 'text-red-500';
      case 'yellow': return 'text-yellow-500';
      case 'blue':
      default: return 'text-blue-500';
    }
  };
  
  /**
   * Default icon based on color if none provided
   */
  const defaultIcon = () => {
    switch (color) {
      case 'green': return <FiCheck className="h-5 w-5" />;
      case 'red': return <FiAlertTriangle className="h-5 w-5" />;
      case 'yellow': return <FiAlertCircle className="h-5 w-5" />;
      case 'blue':
      default: return <FiInfo className="h-5 w-5" />;
    }
  };
  
  return (
    <motion.div
      className={`rounded-lg border ${getBackgroundClass(color)} p-4 mb-6`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      role="alert"
      aria-labelledby={`infocard-title-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex">
        {/* Icon */}
        <div className={`flex-shrink-0 ${getIconColorClass(color)}`}>
          {icon || defaultIcon()}
        </div>
        
        {/* Content */}
        <div className="ml-3 flex-1">
          <h3 
            className="text-sm font-medium text-gray-800"
            id={`infocard-title-${title.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {title}
          </h3>
          <div className="mt-2 text-sm text-gray-700">
            {content}
          </div>
        </div>
        
        {/* Dismiss button */}
        {dismissible && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <motion.button
                type="button"
                className={`inline-flex rounded-md p-1.5 ${getBackgroundClass(color)} ${getIconColorClass(color)}`}
                onClick={() => setIsVisible(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Dismiss"
              >
                <span className="sr-only">Dismiss</span>
                <FiX className="h-4 w-4" />
              </motion.button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default InfoCard;